import { useState } from "react";
import { Check, Calendar, MapPin, Navigation } from "lucide-react";
import { Button } from "../ui/button";
import { toast } from "sonner";
import axios from "axios";
import { useLanguage } from "../../contexts/LanguageContext";
import { API } from "../../lib/config";
import { useRecaptcha } from "../../hooks/useRecaptcha";

export const ContactSection = () => {
  const { language, translations: t } = useLanguage();
  const [formData, setFormData] = useState({ full_name: "", phone: "", message: "", marketing_consent: false });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { getToken } = useRecaptcha();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.full_name || !formData.phone) {
      toast.error(language === 'he' ? "יש למלא שם וטלפון" : "Please fill in name and phone");
      return;
    }
    setIsSubmitting(true);
    try {
      const recaptcha_token = await getToken("contact");
      const response = await axios.post(`${API}/contact`, {
        full_name: formData.full_name,
        phone: formData.phone,
        message: formData.message,
        marketing_consent: formData.marketing_consent,
        recaptcha_token
      });
      if (response.data.success) {
        setIsSubmitted(true);
        setFormData({ full_name: "", phone: "", message: "", marketing_consent: false });
      }
    } catch (error) {
      toast.error(language === 'he' ? "שגיאה בשליחה. נסו שוב או התקשרו ישירות." : "Error sending. Try again or call directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-white scroll-mt-20" data-testid="contact-section">
      <div className="max-w-4xl mx-auto px-4">
        <div className="text-center mb-12">
          <span className="text-[#C4956A] font-semibold mb-2 block">
            {language === 'he' ? 'צור קשר' : 'Contact'}
          </span>
          <h2 className="font-['Heebo'] text-3xl md:text-4xl font-bold text-[#2C2C2C] mb-4">
            {language === 'he' ? 'השאירו פרטים ונחזור אליכם' : 'Leave your details and we\'ll get back to you'}
          </h2>
          <p className="text-lg text-[#2C2C2C]/70">
            {language === 'he' ? 'הייעוץ הראשון ללא עלות' : 'First consultation is free'}
          </p>
        </div>

        {isSubmitted ? (
          <div className="bg-[#FAF7F2] rounded-2xl p-8 text-center max-w-md mx-auto">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="font-['Heebo'] text-xl font-bold text-[#2C2C2C] mb-2">
              {language === 'he' ? 'תודה! קיבלנו את פנייתך' : 'Thank you! We received your inquiry'}
            </h3>
            <p className="text-[#2C2C2C]/70 mb-6">
              {language === 'he' ? 'נחזור אליכם תוך 24 שעות לתיאום פגישה.' : 'We\'ll get back to you within 24 hours to schedule an appointment.'}
            </p>
            <div className="border-t border-[#E5E0DA] pt-6 mt-6">
              <p className="text-sm text-[#2C2C2C]/60 mb-3">
                {language === 'he' ? 'רוצה להזכיר לעצמך?' : 'Want a reminder?'}
              </p>
              <a
                href={`https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(language === 'he' ? 'ייעוץ עם מזל - קוסמטיקאית פרא-רפואית' : 'Consultation with Mazal - Paramedical Cosmetician')}&details=${encodeURIComponent(language === 'he' ? 'ייעוץ ראשוני חינם.\n\nטלפון: 052-3824500\nכתובת: חיים גבריהו 11, פסגת זאב, ירושלים' : 'Free initial consultation.\n\nPhone: 052-3824500\nAddress: Chaim Gevriahu 11, Pisgat Zeev, Jerusalem')}&location=${encodeURIComponent('חיים גבריהו 11, פסגת זאב, ירושלים')}&dates=${(() => {
                  const tomorrow = new Date();
                  tomorrow.setDate(tomorrow.getDate() + 1);
                  tomorrow.setHours(10, 0, 0, 0);
                  const end = new Date(tomorrow);
                  end.setHours(11, 0, 0, 0);
                  const format = (d) => d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
                  return format(tomorrow) + '/' + format(end);
                })()}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-white border-2 border-[#C4956A] text-[#C4956A] hover:bg-[#C4956A] hover:text-white px-6 py-3 rounded-full font-semibold transition-all"
                data-testid="add-to-calendar-btn"
              >
                <Calendar className="w-5 h-5" />
                {language === 'he' ? 'הוסף ליומן Google' : 'Add to Google Calendar'}
              </a>
              <p className="text-xs text-[#2C2C2C]/50 mt-3">
                {language === 'he' ? '* ניתן לשנות את התאריך והשעה ביומן' : '* You can change the date and time in the calendar'}
              </p>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="bg-[#FAF7F2] rounded-2xl p-6 md:p-8 max-w-md mx-auto space-y-4" data-testid="contact-form">
            <div>
              <label htmlFor="full_name" className="block text-sm font-semibold text-[#2C2C2C] mb-2">
                {language === 'he' ? 'שם מלא' : 'Full Name'} *
              </label>
              <input
                type="text"
                id="full_name"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                placeholder={language === 'he' ? 'הכנס/י את שמך' : 'Enter your name'}
                className="w-full rounded-xl border border-[#E5E0DA] py-3 px-4 focus:border-[#C4956A] focus:ring-2 focus:ring-[#C4956A]/20 outline-none"
                required
                data-testid="contact-name"
              />
            </div>
            <div>
              <label htmlFor="phone" className="block text-sm font-semibold text-[#2C2C2C] mb-2">
                {language === 'he' ? 'טלפון' : 'Phone'} *
              </label>
              <input
                type="tel"
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="050-0000000"
                className="w-full rounded-xl border border-[#E5E0DA] py-3 px-4 focus:border-[#C4956A] focus:ring-2 focus:ring-[#C4956A]/20 outline-none"
                required
                data-testid="contact-phone"
              />
            </div>
            <div>
              <label htmlFor="message" className="block text-sm font-semibold text-[#2C2C2C] mb-2">
                {language === 'he' ? 'ספרו לנו קצת על הצלקת (אופציונלי)' : 'Tell us about your scar (optional)'}
              </label>
              <textarea
                id="message"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                placeholder={language === 'he' ? 'סוג הצלקת, גיל, מיקום...' : 'Scar type, age, location...'}
                className="w-full rounded-xl border border-[#E5E0DA] py-3 px-4 min-h-[100px] resize-none focus:border-[#C4956A] focus:ring-2 focus:ring-[#C4956A]/20 outline-none"
                data-testid="contact-message"
              />
            </div>
            <div className="flex items-start gap-2">
              <input
                type="checkbox"
                id="privacy_consent"
                required
                className="mt-1 w-4 h-4 accent-[#C4956A] cursor-pointer"
                data-testid="contact-privacy-consent"
              />
              <label htmlFor="privacy_consent" className="text-xs text-[#2C2C2C]/70 cursor-pointer">
                {language === 'he' 
                  ? 'קראתי ואני מסכים/ה למדיניות הפרטיות. המידע ישמש ליצירת קשר בלבד.'
                  : 'I have read and agree to the Privacy Policy. Information will be used for contact purposes only.'}
              </label>
            </div>
            <div className="flex items-start gap-2">
              <input
                type="checkbox"
                id="marketing_consent"
                checked={formData.marketing_consent}
                onChange={(e) => setFormData({ ...formData, marketing_consent: e.target.checked })}
                className="mt-1 w-4 h-4 accent-[#C4956A] cursor-pointer"
                data-testid="contact-marketing-consent"
              />
              <label htmlFor="marketing_consent" className="text-xs text-[#2C2C2C]/70 cursor-pointer">
                {language === 'he' 
                  ? 'אני מאשר/ת לקבל מידע שיווקי, מבצעים ועדכונים בדוא"ל / SMS / WhatsApp (אופציונלי, ניתן להסיר בכל עת).'
                  : 'I agree to receive marketing, promotions and updates via email/SMS/WhatsApp (optional, can be revoked at any time).'}
              </label>
            </div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full rounded-xl py-4 bg-[#C4956A] hover:bg-[#b38559] text-white text-lg transition-all disabled:opacity-50"
              data-testid="contact-submit"
            >
              {isSubmitting 
                ? (language === 'he' ? 'שולח...' : 'Sending...') 
                : (language === 'he' ? 'שלחו – נחזור אליכם היום' : 'Send – We\'ll contact you today')}
            </button>
            <p className="text-xs text-center text-[#2C2C2C]/50 mt-3">
              {language === 'he' ? 'או התקשרו ישירות: 052-3824500' : 'Or call directly: 052-3824500'}
            </p>
          </form>
        )}
        
        <div className="mt-12 max-w-4xl mx-auto">
          <h3 className="font-['Heebo'] text-xl font-bold text-[#2C2C2C] mb-4 text-center">
            {language === 'he' ? 'מיקום הקליניקה' : 'Clinic Location'}
          </h3>
          <div className="rounded-2xl overflow-hidden shadow-lg mb-4">
            <iframe
              src="https://www.openstreetmap.org/export/embed.html?bbox=35.2375%2C31.8285%2C35.2475%2C31.8365&layer=mapnik&marker=31.8325%2C35.2425"
              width="100%"
              height="300"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              title={language === 'he' ? 'מפת מיקום הקליניקה' : 'Clinic location map'}
              className="w-full"
            />
          </div>
          <div className="bg-[#FAF7F2] rounded-2xl p-6 flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-[#C4956A]/10 flex items-center justify-center">
                <MapPin className="w-6 h-6 text-[#C4956A]" />
              </div>
              <div>
                <p className="font-semibold text-[#2C2C2C]">
                  {language === 'he' ? 'חיים גבריהו 11' : '11 Chaim Gevriahu'}
                </p>
                <p className="text-[#2C2C2C]/60 text-sm">
                  {language === 'he' ? 'פסגת זאב, ירושלים' : 'Pisgat Zeev, Jerusalem'}
                </p>
              </div>
            </div>
            <a
              href="https://www.google.com/maps/dir/?api=1&destination=חיים+גבריהו+11+פסגת+זאב+ירושלים"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#C4956A] hover:bg-[#b38559] text-white px-6 py-3 rounded-full font-semibold transition-all"
              data-testid="get-directions-btn"
            >
              <Navigation className="w-5 h-5" />
              {language === 'he' ? 'נווט לקליניקה' : 'Get Directions'}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};
